/**
 * MaterialsScreen — the signature dual-pane Materials review
 * (`docs/GardEn/handoff-2026-06/garden/screens-materials.jsx` +
 * `_review/desktop-materials.png`).
 *
 * LEFT pane = the faux-scanned Смета rendered by `<DocumentPaper>`; RIGHT pane =
 * the materials list (a 3-up summary, a horizontal category filter, expandable
 * material rows, and a "missing items" dashed card). Desktop (md: ≥768px) is a
 * two-column grid (doc 0.85fr | list 1.15fr) where each column scrolls
 * independently; mobile is a `<Segmented>` Документ|Материалы toggle above a
 * single scrolling pane. A sticky bottom CTA — "Показать продавцов на карте" —
 * navigates to `/shops`.
 *
 * Clicking a line in the document pane focuses + expands that material; on
 * mobile it also switches to the Материалы tab. Data comes from the
 * design-revamp quotes endpoint via `useQuote` — `GET /quotes/{id}` when the
 * `:id` route param is present, else the demo `POST /quotes`. Money/qty arrive
 * as Decimal strings and are parsed with `parseDecimal` before formatting.
 *
 * States: a dual-pane Skeleton on first load; a retryable error card on
 * failure; the populated dual-pane otherwise. Tokens come from the `--g-*`
 * Tailwind mirror; every string is a `materials`-namespace i18n key (ru/ky/en).
 * No PII renders — the quote payload omits the contractor address/phone, and
 * seller matches carry only business name + price.
 *
 * Cross-cutting note: the quote API serialises only an opaque `category_id`
 * UUID (no slug), so the filter pills + row icons are bound to the DISTINCT
 * `category_id`s in payload appearance order (see `category-buckets.ts`). The
 * pairing is exact for the seeded demo's category order and best-effort
 * otherwise; the recommended fix is a `category_slug` on `MaterialItem`.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Card,
  DocumentPaper,
  Icon,
  Pill,
  Segmented,
  Skeleton,
  StatTile,
  type DocumentMaterial,
  type DocumentProject,
} from "@garden/ui";
import { Stagger, StaggerItem } from "@/components/motion";

import { fmtShort, parseDecimal } from "@/lib/format";
import type { MaterialItem, QuoteResponse } from "@/lib/garden-api";
import type { SupportedLanguage } from "@/lib/i18n";
import { registerMaterialsI18n, useQuote } from "@/features/materials/api";
import {
  buildCategoryBuckets,
  iconForMaterial,
  CATEGORY_BUCKETS,
} from "@/features/materials/category-buckets";
import { MaterialRow } from "@/features/materials/MaterialRow";

/** Narrow the i18next language string to a `ru | ky | en` locale (default ru). */
function resolveLang(language: string): SupportedLanguage {
  if (language.startsWith("ky")) return "ky";
  if (language.startsWith("en")) return "en";
  return "ru";
}

/** The `/shops` finder route the sticky CTA navigates to. */
export const SHOPS_ROUTE = "/shops";

/** Map a `QuoteResponse` to the `<DocumentPaper>` project header. The API omits
 * the contractor address/phone (PII), so those render empty; the quote number
 * falls back to the demo label and the date to its ISO string. */
function toDocumentProject(quote: QuoteResponse, fallbackNo: string): DocumentProject {
  return {
    quote: {
      no: quote.quote_number ?? fallbackNo,
      contractor: quote.contractor_name ?? "",
      date: quote.quote_date ?? "",
      addr: "",
      phone: "",
    },
  };
}

/** Map the API materials to `<DocumentPaper>` line items (qty parsed to a
 * number; names/unit are localized bags the paper picks from). */
function toDocumentMaterials(materials: readonly MaterialItem[]): DocumentMaterial[] {
  return materials.map((m) => ({
    id: m.id,
    line: m.line_no,
    name: m.names,
    qty: parseDecimal(m.qty),
    unit: m.unit,
  }));
}

interface MaterialsListProps {
  quote: QuoteResponse;
  lang: SupportedLanguage;
  expandedId: string | null;
  onToggle: (id: string) => void;
}

/** The right-pane content: summary tiles, filter pills, rows, missing card. */
function MaterialsList({
  quote,
  lang,
  expandedId,
  onToggle,
}: MaterialsListProps): ReactElement {
  const { t } = useTranslation("materials");
  const [filterIndex, setFilterIndex] = useState<number>(0);

  const buckets = useMemo(
    () => buildCategoryBuckets(quote.materials),
    [quote.materials],
  );

  // Filter index 0 = "Все"; 1..N filter to the i-th distinct category_id.
  const filtered = useMemo<MaterialItem[]>(() => {
    if (filterIndex === 0) return quote.materials;
    const targetCategoryId = buckets.orderedCategoryIds[filterIndex - 1];
    if (targetCategoryId === undefined) return quote.materials;
    return quote.materials.filter((m) => m.category_id === targetCategoryId);
  }, [filterIndex, quote.materials, buckets.orderedCategoryIds]);

  // Only show pills that have a backing category in the data (plus "Все").
  const categoryPillCount = Math.min(
    buckets.orderedCategoryIds.length,
    CATEGORY_BUCKETS.length,
  );

  const summary = quote.summary;
  const minTotal = parseDecimal(summary.min_total_kgs);
  const estTotal = parseDecimal(summary.est_kgs);
  const som = t("summary.som");

  const missingKeys = ["underlay", "skirting", "sockets"] as const;

  return (
    <div>
      {/* 3-up summary */}
      <div className="mb-4 grid grid-cols-3 gap-3" data-testid="materials-summary">
        <Card padding={14}>
          <StatTile label={t("summary.positions")} value={summary.positions} />
        </Card>
        <Card padding={14}>
          <StatTile label={t("summary.minimum")} value={fmtShort(minTotal)} sub={som} />
        </Card>
        <Card padding={14}>
          <StatTile label={t("summary.choice")} value={fmtShort(estTotal)} sub={som} />
        </Card>
      </div>

      {/* Category filter row */}
      <div
        role="group"
        aria-label={t("filters.label")}
        data-testid="materials-filters"
        className="-mx-5 mb-3.5 flex gap-2 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <Pill
          active={filterIndex === 0}
          onClick={() => setFilterIndex(0)}
        >
          {t("filters.all")}
        </Pill>
        {CATEGORY_BUCKETS.slice(0, categoryPillCount).map((bucket, i) => (
          <Pill
            key={bucket.labelKey}
            active={filterIndex === i + 1}
            onClick={() => setFilterIndex(i + 1)}
          >
            {t(`filters.${bucket.labelKey}`)}
          </Pill>
        ))}
      </div>

      {/* Material rows */}
      <Stagger className="flex flex-col gap-2.5" data-testid="materials-rows">
        {filtered.map((material) => (
          <StaggerItem key={material.id}>
            <MaterialRow
              material={material}
              lang={lang}
              icon={iconForMaterial(material, buckets)}
              expanded={expandedId === material.id}
              onToggle={() => onToggle(material.id)}
            />
          </StaggerItem>
        ))}
      </Stagger>

      {/* Missing items */}
      <div
        data-testid="materials-missing"
        className="mt-3.5 rounded-[var(--g-radius)] border border-dashed border-border bg-[var(--g-ground)] p-4"
      >
        <div className="mb-2 flex items-center gap-2 text-[var(--g-warn)]">
          <Icon name="info" size={16} />
          <span className="text-[13.5px] font-bold">{t("missing")}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {missingKeys.map((key) => (
            <Pill key={key} tone="warn" size="sm" leading="plus">
              {t(`missingItems.${key}`)}
            </Pill>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Dual-pane skeleton matching the loaded layout (left doc, right list). */
function MaterialsSkeleton(): ReactElement {
  return (
    <div
      data-testid="materials-skeleton"
      className="grid flex-1 grid-cols-1 gap-6 overflow-hidden p-4 md:grid-cols-[0.85fr_1.15fr] md:gap-0 md:p-0"
    >
      {/* Document pane skeleton */}
      <div className="md:overflow-y-auto md:border-r md:border-border md:p-6">
        <div className="rounded-[var(--g-radius)] border border-border bg-[var(--g-surface)] p-[18px]">
          <Skeleton w="40%" h={14} />
          <div className="mt-2 flex flex-col gap-1.5">
            <Skeleton w="70%" h={10} />
            <Skeleton w="55%" h={10} />
          </div>
          <div className="mt-4 flex flex-col gap-2.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} w="100%" h={14} />
            ))}
          </div>
        </div>
      </div>
      {/* List pane skeleton */}
      <div className="md:overflow-y-auto md:p-6">
        <div className="mb-4 grid grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} w="100%" h={64} r={14} />
          ))}
        </div>
        <div className="mb-3.5 flex gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} w={72} h={32} r={999} />
          ))}
        </div>
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} w="100%" h={72} r={14} />
          ))}
        </div>
      </div>
    </div>
  );
}

export interface MaterialsScreenProps {
  /** Override the route `:id` param (testing/composition); falls back to the
   * `useParams` value. */
  quoteId?: string;
}

/**
 * The Materials dual-pane screen. Routed at `/materials` (demo `POST /quotes`)
 * and `/project/materials/:id` (`GET /quotes/{id}`). Export name `MaterialsScreen`.
 */
export function MaterialsScreen({ quoteId }: MaterialsScreenProps = {}): ReactElement {
  registerMaterialsI18n();
  const { t, i18n } = useTranslation("materials");
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const lang = resolveLang(i18n.language);

  const resolvedId = quoteId ?? params.id;
  const { data: quote, isLoading, isError, refetch } = useQuote(resolvedId);

  // Mobile pane toggle (doc | list) + focused/expanded material.
  const [tab, setTab] = useState<string>("list");
  const [focusId, setFocusId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Once the quote resolves, focus + expand the first material (design default).
  useEffect(() => {
    if (quote && quote.materials.length > 0 && focusId === null) {
      const first = quote.materials[0];
      if (first !== undefined) {
        setFocusId(first.id);
        setExpandedId(first.id);
      }
    }
  }, [quote, focusId]);

  const toggleRow = (id: string): void => {
    setExpandedId((current) => (current === id ? null : id));
    setFocusId(id);
  };

  // A document-line click focuses + expands the material; on mobile it also
  // switches to the Материалы tab so the expansion is visible.
  const handleLineClick = (id: string): void => {
    setFocusId(id);
    setExpandedId(id);
    setTab("list");
  };

  const documentProject = useMemo(
    () => (quote ? toDocumentProject(quote, t("quoteCard")) : null),
    [quote, t],
  );
  const documentMaterials = useMemo(
    () => (quote ? toDocumentMaterials(quote.materials) : []),
    [quote],
  );

  const subtitle = quote
    ? t("subtitle", {
        quote: t("quoteCard"),
        contractor: quote.contractor_name ?? "",
      })
    : "";

  const documentPane =
    documentProject !== null ? (
      <DocumentPaper
        project={documentProject}
        materials={documentMaterials}
        focusId={focusId ?? undefined}
        lang={lang}
        onLineClick={handleLineClick}
      />
    ) : null;

  return (
    <div
      data-testid="materials-screen"
      className="flex h-full min-h-full flex-col bg-[var(--g-ground)]"
    >
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-border bg-[var(--g-surface)] px-5 py-3.5">
        <button
          type="button"
          data-testid="materials-back"
          onClick={() => navigate(-1)}
          aria-label={t("back")}
          className="-m-[3px] grid size-11 shrink-0 place-items-center rounded-[var(--g-radius)] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {/* 38px visual disc per the design, inside a 44px hit area (the
              negative margin keeps the layout flush). */}
          <span className="grid size-[38px] place-items-center rounded-[var(--g-radius)] bg-[var(--g-ground)]">
            <Icon name="chevL" size={18} />
          </span>
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-bold tracking-[-0.01em] text-foreground">
            {t("title")}
          </h1>
          {subtitle !== "" && (
            <p className="truncate text-xs text-[var(--g-ink-3)]">{subtitle}</p>
          )}
        </div>
      </header>

      {isError ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <div
            role="alert"
            data-testid="materials-error"
            className="flex max-w-sm flex-col items-center gap-3 rounded-[var(--g-radius)] border border-border bg-[var(--g-surface)] p-6 text-center"
          >
            <Icon name="warn" size={22} className="text-[var(--g-warn)]" />
            <p className="text-sm text-foreground">{t("states.error")}</p>
            <Button
              variant="outline"
              size="sm"
              leading="arrowR"
              data-testid="materials-retry"
              onClick={() => {
                void refetch();
              }}
            >
              {t("states.retry")}
            </Button>
          </div>
        </div>
      ) : isLoading || quote === undefined ? (
        <MaterialsSkeleton />
      ) : (
        <>
          {/* Desktop: two independently-scrolling columns. */}
          <div className="hidden flex-1 overflow-hidden md:grid md:grid-cols-[0.85fr_1.15fr]">
            <div className="overflow-y-auto border-r border-border p-6">
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.05em] text-[var(--g-ink-3)]">
                {t("docLabel")}
              </div>
              {documentPane}
            </div>
            <div className="overflow-y-auto p-6" data-testid="materials-list-desktop">
              <MaterialsList
                quote={quote}
                lang={lang}
                expandedId={expandedId}
                onToggle={toggleRow}
              />
            </div>
          </div>

          {/* Mobile: a Документ | Материалы toggle over one scrolling pane. */}
          <div className="flex flex-1 flex-col overflow-hidden md:hidden">
            <div className="px-5 pt-3.5">
              <Segmented
                full
                options={[
                  { value: "doc", label: t("tabs.doc") },
                  { value: "list", label: t("tabs.list") },
                ]}
                value={tab}
                onChange={setTab}
              />
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-4 pt-4" data-testid="materials-pane-mobile">
              {tab === "doc" ? (
                documentPane
              ) : (
                <MaterialsList
                  quote={quote}
                  lang={lang}
                  expandedId={expandedId}
                  onToggle={toggleRow}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Sticky bottom CTA → /shops */}
      <div className="sticky bottom-0 border-t border-border bg-[var(--g-surface)] px-5 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] md:static md:pb-3">
        <div className="mx-auto max-w-[720px]">
          <Button
            full
            size="lg"
            leading="pin"
            data-testid="materials-cta"
            onClick={() => navigate(SHOPS_ROUTE)}
          >
            {t("cta")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MaterialsScreen;
